"use strict";

/**
  * @reach/combobox v0.18.0
  *
  * Copyright (c) 2018-2022, React Training LLC
  *
  * This source code is licensed under the MIT license found in the
  * LICENSE.md file in the root directory of this source tree.
  *
  * @license MIT
  */

// src/reach-combobox.tsx
import "core-js/modules/es.object.keys.js";
import "core-js/modules/es.symbol.js";
import "core-js/modules/es.object.get-own-property-descriptor.js";
import "core-js/modules/es.object.get-own-property-descriptors.js";
import "core-js/modules/es.object.define-properties.js";
import "core-js/modules/es.object.define-property.js";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
var _excluded = ["onSelect", "openOnFocus", "children", "as", "aria-label", "aria-labelledby"],
  _excluded2 = ["as", "selectOnClick", "autocomplete", "onClick", "onChange", "onKeyDown", "onBlur", "onFocus", "value"],
  _excluded3 = ["as", "children", "portal", "onKeyDown", "onBlur", "position"],
  _excluded4 = ["persistSelection", "as"],
  _excluded5 = ["as", "children", "index", "value", "onClick"],
  _excluded6 = ["as", "onClick", "onKeyDown"];
var _on, _on2, _on3, _on4, _states;
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import "core-js/modules/es.array.reduce.js";
import "core-js/modules/es.object.to-string.js";
import "core-js/modules/es.array.sort.js";
import "core-js/modules/es.array.push.js";
import "core-js/modules/es.array.filter.js";
import "core-js/modules/es.regexp.constructor.js";
import "core-js/modules/es.regexp.dot-all.js";
import "core-js/modules/es.regexp.exec.js";
import "core-js/modules/es.regexp.sticky.js";
import "core-js/modules/es.regexp.to-string.js";
import "core-js/modules/web.dom-collections.for-each.js";
import "core-js/modules/es.string.replace.js";
import "core-js/modules/es.array.includes.js";
import "core-js/modules/es.string.trim.js";
import "core-js/modules/es.string.split.js";
import "core-js/modules/es.array.map.js";
import "core-js/modules/es.array.slice.js";
import "core-js/modules/es.array.find-index.js";
import "core-js/modules/es.string.includes.js";
import * as React from "react";
import { createNamedContext, composeEventHandlers, isFunction, makeId, noop, useComposedRefs, useIsomorphicLayoutEffect as useLayoutEffect, useStatefulRefValue, useUpdateEffect } from "@reach/utils";
import { createDescendantContext, DescendantProvider, useDescendant, useDescendants, useDescendantsInit } from "@reach/descendants";

// src/utils.ts
function findAll(_ref) {
  var autoEscape = _ref.autoEscape,
    _ref$caseSensitive = _ref.caseSensitive,
    caseSensitive = _ref$caseSensitive === void 0 ? false : _ref$caseSensitive,
    _ref$findChunks = _ref.findChunks,
    findChunks = _ref$findChunks === void 0 ? defaultFindChunks : _ref$findChunks,
    sanitize = _ref.sanitize,
    searchWords = _ref.searchWords,
    textToHighlight = _ref.textToHighlight;
  return fillInChunks({
    chunksToHighlight: combineChunks({
      chunks: findChunks({
        autoEscape: autoEscape,
        caseSensitive: caseSensitive,
        sanitize: sanitize,
        searchWords: searchWords,
        textToHighlight: textToHighlight
      })
    }),
    totalLength: textToHighlight ? textToHighlight.length : 0
  });
}
function combineChunks(_ref2) {
  var chunks = _ref2.chunks;
  return chunks.sort(function (first, second) {
    return first.start - second.start;
  }).reduce(function (processedChunks, nextChunk) {
    if (processedChunks.length === 0) {
      return [nextChunk];
    } else {
      var prevChunk = processedChunks.pop();
      if (nextChunk.start <= prevChunk.end) {
        var endIndex = Math.max(prevChunk.end, nextChunk.end);
        processedChunks.push({
          highlight: false,
          start: prevChunk.start,
          end: endIndex
        });
      } else {
        processedChunks.push(prevChunk, nextChunk);
      }
      return processedChunks;
    }
  }, []);
}
function defaultFindChunks(_ref3) {
  var autoEscape = _ref3.autoEscape,
    caseSensitive = _ref3.caseSensitive,
    _ref3$sanitize = _ref3.sanitize,
    sanitize = _ref3$sanitize === void 0 ? defaultSanitize : _ref3$sanitize,
    searchWords = _ref3.searchWords,
    textToHighlight = _ref3.textToHighlight;
  textToHighlight = sanitize(textToHighlight || "");
  return searchWords.filter(function (searchWord) {
    return searchWord;
  }).reduce(function (chunks, searchWord) {
    searchWord = sanitize(searchWord);
    if (autoEscape) {
      searchWord = escapeRegExpFn(searchWord);
    }
    var regex = new RegExp(searchWord, caseSensitive ? "g" : "gi");
    var match;
    while (match = regex.exec(textToHighlight || "")) {
      var start = match.index;
      var end = regex.lastIndex;
      if (end > start) {
        chunks.push({
          highlight: false,
          start: start,
          end: end
        });
      }
      if (match.index === regex.lastIndex) {
        regex.lastIndex++;
      }
    }
    return chunks;
  }, []);
}
function fillInChunks(_ref4) {
  var chunksToHighlight = _ref4.chunksToHighlight,
    totalLength = _ref4.totalLength;
  var allChunks = [];
  if (chunksToHighlight.length === 0) {
    append(0, totalLength, false);
  } else {
    var lastIndex = 0;
    chunksToHighlight.forEach(function (chunk) {
      append(lastIndex, chunk.start, false);
      append(chunk.start, chunk.end, true);
      lastIndex = chunk.end;
    });
    append(lastIndex, totalLength, false);
  }
  return allChunks;
  function append(start, end, highlight) {
    if (end - start > 0) {
      allChunks.push({
        start: start,
        end: end,
        highlight: highlight
      });
    }
  }
}
function defaultSanitize(string) {
  return string;
}
function escapeRegExpFn(string) {
  return string.replace(/[-[\]/{}()*+?.\\^$|]/g, "\\$&");
}
var HighlightWords = {
  combineChunks: combineChunks,
  fillInChunks: fillInChunks,
  findAll: findAll,
  findChunks: defaultFindChunks
};

// src/reach-combobox.tsx
import { useId } from "@reach/auto-id";
import { Popover, positionMatchWidth } from "@reach/popover";
var IDLE = "IDLE";
var SUGGESTING = "SUGGESTING";
var NAVIGATING = "NAVIGATING";
var INTERACTING = "INTERACTING";
var CLEAR = "CLEAR";
var CHANGE = "CHANGE";
var INITIAL_CHANGE = "INITIAL_CHANGE";
var NAVIGATE = "NAVIGATE";
var SELECT_WITH_KEYBOARD = "SELECT_WITH_KEYBOARD";
var SELECT_WITH_CLICK = "SELECT_WITH_CLICK";
var ESCAPE = "ESCAPE";
var BLUR = "BLUR";
var INTERACT = "INTERACT";
var FOCUS = "FOCUS";
var OPEN_WITH_BUTTON = "OPEN_WITH_BUTTON";
var OPEN_WITH_INPUT_CLICK = "OPEN_WITH_INPUT_CLICK";
var CLOSE_WITH_BUTTON = "CLOSE_WITH_BUTTON";
var stateChart = {
  initial: IDLE,
  states: (_states = {}, _defineProperty(_states, IDLE, {
    on: (_on = {}, _defineProperty(_on, BLUR, IDLE), _defineProperty(_on, CLEAR, IDLE), _defineProperty(_on, CHANGE, SUGGESTING), _defineProperty(_on, INITIAL_CHANGE, IDLE), _defineProperty(_on, FOCUS, SUGGESTING), _defineProperty(_on, NAVIGATE, NAVIGATING), _defineProperty(_on, OPEN_WITH_BUTTON, SUGGESTING), _defineProperty(_on, OPEN_WITH_INPUT_CLICK, SUGGESTING), _on)
  }), _defineProperty(_states, SUGGESTING, {
    on: (_on2 = {}, _defineProperty(_on2, CHANGE, SUGGESTING), _defineProperty(_on2, FOCUS, SUGGESTING), _defineProperty(_on2, NAVIGATE, NAVIGATING), _defineProperty(_on2, CLEAR, IDLE), _defineProperty(_on2, ESCAPE, IDLE), _defineProperty(_on2, BLUR, IDLE), _defineProperty(_on2, SELECT_WITH_CLICK, IDLE), _defineProperty(_on2, INTERACT, INTERACTING), _defineProperty(_on2, CLOSE_WITH_BUTTON, IDLE), _on2)
  }), _defineProperty(_states, NAVIGATING, {
    on: (_on3 = {}, _defineProperty(_on3, CHANGE, SUGGESTING), _defineProperty(_on3, FOCUS, SUGGESTING), _defineProperty(_on3, CLEAR, IDLE), _defineProperty(_on3, BLUR, IDLE), _defineProperty(_on3, ESCAPE, IDLE), _defineProperty(_on3, NAVIGATE, NAVIGATING), _defineProperty(_on3, SELECT_WITH_CLICK, IDLE), _defineProperty(_on3, SELECT_WITH_KEYBOARD, IDLE), _defineProperty(_on3, CLOSE_WITH_BUTTON, IDLE), _defineProperty(_on3, INTERACT, INTERACTING), _on3)
  }), _defineProperty(_states, INTERACTING, {
    on: (_on4 = {}, _defineProperty(_on4, CLEAR, IDLE), _defineProperty(_on4, CHANGE, SUGGESTING), _defineProperty(_on4, FOCUS, SUGGESTING), _defineProperty(_on4, BLUR, IDLE), _defineProperty(_on4, ESCAPE, IDLE), _defineProperty(_on4, NAVIGATE, NAVIGATING), _defineProperty(_on4, CLOSE_WITH_BUTTON, IDLE), _defineProperty(_on4, SELECT_WITH_CLICK, IDLE), _on4)
  }), _states)
};
var reducer = function reducer(data, event) {
  var nextState = _objectSpread(_objectSpread({}, data), {}, {
    lastEventType: event.type
  });
  switch (event.type) {
    case CHANGE:
    case INITIAL_CHANGE:
      return _objectSpread(_objectSpread({}, nextState), {}, {
        navigationValue: null,
        value: event.value
      });
    case NAVIGATE:
    case OPEN_WITH_BUTTON:
    case OPEN_WITH_INPUT_CLICK:
      return _objectSpread(_objectSpread({}, nextState), {}, {
        navigationValue: findNavigationValue(nextState, event)
      });
    case CLEAR:
      return _objectSpread(_objectSpread({}, nextState), {}, {
        value: "",
        navigationValue: null
      });
    case BLUR:
    case ESCAPE:
      return _objectSpread(_objectSpread({}, nextState), {}, {
        navigationValue: null
      });
    case SELECT_WITH_CLICK:
      return _objectSpread(_objectSpread({}, nextState), {}, {
        value: event.isControlled ? data.value : event.value,
        navigationValue: null
      });
    case SELECT_WITH_KEYBOARD:
      return _objectSpread(_objectSpread({}, nextState), {}, {
        value: event.isControlled ? data.value : data.navigationValue,
        navigationValue: null
      });
    case CLOSE_WITH_BUTTON:
      return _objectSpread(_objectSpread({}, nextState), {}, {
        navigationValue: null
      });
    case INTERACT:
      return nextState;
    case FOCUS:
      return _objectSpread(_objectSpread({}, nextState), {}, {
        navigationValue: findNavigationValue(nextState, event)
      });
    default:
      return nextState;
  }
};
function popoverIsExpanded(state) {
  return [SUGGESTING, NAVIGATING, INTERACTING].includes(state);
}
function findNavigationValue(stateData, event) {
  if (event.value) {
    return event.value;
  } else if (event.persistSelection) {
    return stateData.value;
  } else {
    return null;
  }
}
var ComboboxDescendantContext = createDescendantContext("ComboboxDescendantContext");
var ComboboxContext = createNamedContext("ComboboxContext", {});
var OptionContext = createNamedContext("OptionContext", {});
var Combobox = /*#__PURE__*/React.forwardRef(function (_ref5, forwardedRef) {
  var _data$navigationValue;
  var onSelect = _ref5.onSelect,
    _ref5$openOnFocus = _ref5.openOnFocus,
    openOnFocus = _ref5$openOnFocus === void 0 ? false : _ref5$openOnFocus,
    children = _ref5.children,
    _ref5$as = _ref5.as,
    Comp = _ref5$as === void 0 ? "div" : _ref5$as,
    ariaLabel = _ref5["aria-label"],
    ariaLabelledby = _ref5["aria-labelledby"],
    props = _objectWithoutProperties(_ref5, _excluded);
  var _useDescendantsInit = useDescendantsInit(),
    _useDescendantsInit2 = _slicedToArray(_useDescendantsInit, 2),
    options = _useDescendantsInit2[0],
    setOptions = _useDescendantsInit2[1];
  var inputRef = React.useRef();
  var popoverRef = React.useRef();
  var buttonRef = React.useRef();
  var autocompletePropRef = React.useRef(false);
  var persistSelectionRef = React.useRef(false);
  var defaultData = {
    value: "",
    navigationValue: null
  };
  var _useReducerMachine = useReducerMachine(stateChart, reducer, defaultData),
    _useReducerMachine2 = _slicedToArray(_useReducerMachine, 3),
    state = _useReducerMachine2[0],
    data = _useReducerMachine2[1],
    transition = _useReducerMachine2[2];
  useFocusManagement(data.lastEventType, inputRef);
  var id = useId(props.id);
  var listboxId = id ? makeId("listbox", id) : "listbox";
  var isControlledRef = React.useRef(false);
  var isExpanded = popoverIsExpanded(state);
  var context = {
    ariaLabel: ariaLabel,
    ariaLabelledby: ariaLabelledby,
    autocompletePropRef: autocompletePropRef,
    buttonRef: buttonRef,
    comboboxId: id,
    data: data,
    inputRef: inputRef,
    isExpanded: isExpanded,
    listboxId: listboxId,
    onSelect: onSelect || noop,
    openOnFocus: openOnFocus,
    persistSelectionRef: persistSelectionRef,
    popoverRef: popoverRef,
    state: state,
    transition: transition,
    isControlledRef: isControlledRef
  };
  return /* @__PURE__ */React.createElement(DescendantProvider, {
    context: ComboboxDescendantContext,
    items: options,
    set: setOptions
  }, /* @__PURE__ */React.createElement(ComboboxContext.Provider, {
    value: context
  }, /* @__PURE__ */React.createElement(Comp, _objectSpread(_objectSpread({}, props), {}, {
    "data-reach-combobox": "",
    "data-state": getDataState(state),
    "data-expanded": isExpanded || void 0,
    ref: forwardedRef
  }), isFunction(children) ? children({
    id: id,
    isExpanded: isExpanded,
    navigationValue: (_data$navigationValue = data.navigationValue) !== null && _data$navigationValue !== void 0 ? _data$navigationValue : null,
    state: state
  }) : children)));
});
Combobox.displayName = "Combobox";
var ComboboxInput = /*#__PURE__*/React.forwardRef(function (_ref6, forwardedRef) {
  var _ref6$as = _ref6.as,
    Comp = _ref6$as === void 0 ? "input" : _ref6$as,
    _ref6$selectOnClick = _ref6.selectOnClick,
    selectOnClick = _ref6$selectOnClick === void 0 ? false : _ref6$selectOnClick,
    _ref6$autocomplete = _ref6.autocomplete,
    autocomplete = _ref6$autocomplete === void 0 ? true : _ref6$autocomplete,
    onClick = _ref6.onClick,
    onChange = _ref6.onChange,
    onKeyDown = _ref6.onKeyDown,
    onBlur = _ref6.onBlur,
    onFocus = _ref6.onFocus,
    controlledValue = _ref6.value,
    props = _objectWithoutProperties(_ref6, _excluded2);
  var _React$useRef = React.useRef(controlledValue),
    initialControlledValue = _React$useRef.current;
  var controlledValueChangedRef = React.useRef(false);
  useUpdateEffect(function () {
    controlledValueChangedRef.current = true;
  }, [controlledValue]);
  var _React$useContext = React.useContext(ComboboxContext),
    _React$useContext$dat = _React$useContext.data,
    navigationValue = _React$useContext$dat.navigationValue,
    value = _React$useContext$dat.value,
    lastEventType = _React$useContext$dat.lastEventType,
    inputRef = _React$useContext.inputRef,
    state = _React$useContext.state,
    transition = _React$useContext.transition,
    listboxId = _React$useContext.listboxId,
    autocompletePropRef = _React$useContext.autocompletePropRef,
    openOnFocus = _React$useContext.openOnFocus,
    isExpanded = _React$useContext.isExpanded,
    ariaLabel = _React$useContext.ariaLabel,
    ariaLabelledby = _React$useContext.ariaLabelledby,
    persistSelectionRef = _React$useContext.persistSelectionRef,
    isControlledRef = _React$useContext.isControlledRef;
  var ref = useComposedRefs(inputRef, forwardedRef);
  var selectOnClickRef = React.useRef(false);
  var handleKeyDown = useKeyDown();
  var handleBlur = useBlur();
  var isControlled = typeof controlledValue !== "undefined";
  var wasInitiallyControlled = typeof initialControlledValue !== "undefined";
  if (true) {
    if (!isControlled && wasInitiallyControlled) {
      console.warn("ComboboxInput is changing from controlled to uncontrolled. ComboboxInput should not switch from controlled to uncontrolled (or vice versa). Decide between using a controlled or uncontrolled ComboboxInput for the lifetime of the component. Check the `value` prop being passed in.");
    }
    if (isControlled && !wasInitiallyControlled) {
      console.warn("ComboboxInput is changing from uncontrolled to controlled. ComboboxInput should not switch from controlled to uncontrolled (or vice versa). Decide between using a controlled or uncontrolled ComboboxInput for the lifetime of the component. Check the `value` prop being passed in.");
    }
  }
  React.useEffect(function () {
    isControlledRef.current = isControlled;
  }, [isControlled]);
  useLayoutEffect(function () {
    autocompletePropRef.current = autocomplete;
  }, [autocomplete, autocompletePropRef]);
  var handleValueChange = React.useCallback(function (value2) {
    if (value2.trim() === "") {
      transition(CLEAR, {
        isControlled: isControlled
      });
    } else if (value2 === initialControlledValue && !controlledValueChangedRef.current) {
      transition(INITIAL_CHANGE, {
        value: value2
      });
    } else {
      transition(CHANGE, {
        value: value2
      });
    }
  }, [initialControlledValue, transition, isControlled]);
  React.useEffect(function () {
    if (isControlled && controlledValue !== value && (controlledValue.trim() === "" ? (value || "").trim() !== "" : true)) {
      handleValueChange(controlledValue);
    }
  }, [controlledValue, handleValueChange, isControlled, value]);
  React.useEffect(function () {
    var _inputRef$current;
    var form = (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 ? void 0 : _inputRef$current.form;
    if (!form) return;
    function handleReset(event) {
      transition(CLEAR, {
        isControlled: isControlled
      });
    }
    form.addEventListener("reset", handleReset);
    return function () {
      form === null || form === void 0 ? void 0 : form.removeEventListener("reset", handleReset);
    };
  }, [inputRef, isControlled, transition]);
  function handleChange(event) {
    var value2 = event.target.value;
    if (!isControlled) {
      handleValueChange(value2);
    }
  }
  function handleFocus() {
    if (selectOnClick) {
      selectOnClickRef.current = true;
    }
    if (openOnFocus && lastEventType !== SELECT_WITH_CLICK) {
      transition(FOCUS, {
        persistSelection: persistSelectionRef.current
      });
    }
  }
  function handleClick() {
    if (selectOnClickRef.current) {
      var _inputRef$current2;
      selectOnClickRef.current = false;
      (_inputRef$current2 = inputRef.current) === null || _inputRef$current2 === void 0 ? void 0 : _inputRef$current2.select();
    }
    if (openOnFocus && state === IDLE) {
      transition(OPEN_WITH_INPUT_CLICK);
    }
  }
  var inputValue = autocomplete && (state === NAVIGATING || state === INTERACTING) ? navigationValue || controlledValue || value : controlledValue || value;
  return /* @__PURE__ */React.createElement(Comp, _objectSpread(_objectSpread({
    "aria-activedescendant": navigationValue ? String(makeHash(navigationValue)) : void 0,
    "aria-autocomplete": "both",
    "aria-controls": listboxId,
    "aria-expanded": isExpanded,
    "aria-haspopup": "listbox",
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabel ? void 0 : ariaLabelledby,
    role: "combobox"
  }, props), {}, {
    "data-reach-combobox-input": "",
    "data-state": getDataState(state),
    ref: ref,
    onBlur: composeEventHandlers(onBlur, handleBlur),
    onChange: composeEventHandlers(onChange, handleChange),
    onClick: composeEventHandlers(onClick, handleClick),
    onFocus: composeEventHandlers(onFocus, handleFocus),
    onKeyDown: composeEventHandlers(onKeyDown, handleKeyDown),
    value: inputValue || ""
  }));
});
ComboboxInput.displayName = "ComboboxInput";
var ComboboxPopover = /*#__PURE__*/React.forwardRef(function (_ref7, forwardedRef) {
  var _ref7$as = _ref7.as,
    Comp = _ref7$as === void 0 ? "div" : _ref7$as,
    children = _ref7.children,
    _ref7$portal = _ref7.portal,
    portal = _ref7$portal === void 0 ? true : _ref7$portal,
    onKeyDown = _ref7.onKeyDown,
    onBlur = _ref7.onBlur,
    _ref7$position = _ref7.position,
    position = _ref7$position === void 0 ? positionMatchWidth : _ref7$position,
    props = _objectWithoutProperties(_ref7, _excluded3);
  var _React$useContext2 = React.useContext(ComboboxContext),
    popoverRef = _React$useContext2.popoverRef,
    inputRef = _React$useContext2.inputRef,
    isExpanded = _React$useContext2.isExpanded,
    state = _React$useContext2.state;
  var ref = useComposedRefs(popoverRef, forwardedRef);
  var handleKeyDown = useKeyDown();
  var handleBlur = useBlur();
  var sharedProps = {
    "data-reach-combobox-popover": "",
    "data-state": getDataState(state),
    onKeyDown: composeEventHandlers(onKeyDown, handleKeyDown),
    onBlur: composeEventHandlers(onBlur, handleBlur),
    hidden: !isExpanded,
    tabIndex: -1,
    children: children
  };
  return portal ? /* @__PURE__ */React.createElement(Popover, _objectSpread(_objectSpread({
    as: Comp
  }, props), {}, {
    ref: ref,
    "data-expanded": isExpanded || void 0,
    position: position,
    targetRef: inputRef,
    unstable_skipInitialPortalRender: true
  }, sharedProps)) : /* @__PURE__ */React.createElement(Comp, _objectSpread(_objectSpread({
    ref: ref
  }, props), sharedProps));
});
ComboboxPopover.displayName = "ComboboxPopover";
var ComboboxList = /*#__PURE__*/React.forwardRef(function (_ref8, forwardedRef) {
  var _ref8$persistSelectio = _ref8.persistSelection,
    persistSelection = _ref8$persistSelectio === void 0 ? false : _ref8$persistSelectio,
    _ref8$as = _ref8.as,
    Comp = _ref8$as === void 0 ? "ul" : _ref8$as,
    props = _objectWithoutProperties(_ref8, _excluded4);
  var _React$useContext3 = React.useContext(ComboboxContext),
    persistSelectionRef = _React$useContext3.persistSelectionRef,
    listboxId = _React$useContext3.listboxId;
  if (persistSelection) {
    persistSelectionRef.current = true;
  }
  return /* @__PURE__ */React.createElement(Comp, _objectSpread(_objectSpread({
    role: "listbox"
  }, props), {}, {
    ref: forwardedRef,
    "data-reach-combobox-list": "",
    id: listboxId
  }));
});
ComboboxList.displayName = "ComboboxList";
var ComboboxOption = /*#__PURE__*/React.forwardRef(function (_ref9, forwardedRef) {
  var _ref9$as = _ref9.as,
    Comp = _ref9$as === void 0 ? "li" : _ref9$as,
    children = _ref9.children,
    indexProp = _ref9.index,
    value = _ref9.value,
    onClick = _ref9.onClick,
    props = _objectWithoutProperties(_ref9, _excluded5);
  var _React$useContext4 = React.useContext(ComboboxContext),
    onSelect = _React$useContext4.onSelect,
    navigationValue = _React$useContext4.data.navigationValue,
    transition = _React$useContext4.transition,
    isControlledRef = _React$useContext4.isControlledRef;
  var ownRef = React.useRef(null);
  var _useStatefulRefValue = useStatefulRefValue(ownRef, null),
    _useStatefulRefValue2 = _slicedToArray(_useStatefulRefValue, 2),
    element = _useStatefulRefValue2[0],
    handleRefSet = _useStatefulRefValue2[1];
  var descendant = React.useMemo(function () {
    return {
      element: element,
      value: value
    };
  }, [value, element]);
  var index = useDescendant(descendant, ComboboxDescendantContext, indexProp);
  var ref = useComposedRefs(forwardedRef, handleRefSet);
  var isActive = navigationValue === value;
  var handleClick = function handleClick() {
    onSelect && onSelect(value);
    transition(SELECT_WITH_CLICK, {
      value: value,
      isControlled: isControlledRef.current
    });
  };
  return /* @__PURE__ */React.createElement(OptionContext.Provider, {
    value: {
      value: value,
      index: index
    }
  }, /* @__PURE__ */React.createElement(Comp, _objectSpread(_objectSpread({
    "aria-selected": isActive,
    role: "option"
  }, props), {}, {
    "data-reach-combobox-option": "",
    ref: ref,
    id: String(makeHash(value)),
    "data-highlighted": isActive ? "" : void 0,
    tabIndex: -1,
    onClick: composeEventHandlers(onClick, handleClick)
  }), children ? isFunction(children) ? children({
    value: value,
    index: index
  }) : children : /* @__PURE__ */React.createElement(ComboboxOptionText, null)));
});
ComboboxOption.displayName = "ComboboxOption";
function ComboboxOptionText() {
  var _React$useContext5 = React.useContext(OptionContext),
    value = _React$useContext5.value;
  var _React$useContext6 = React.useContext(ComboboxContext),
    contextValue = _React$useContext6.data.value;
  var results = React.useMemo(function () {
    return HighlightWords.findAll({
      searchWords: escapeRegexp(contextValue || "").split(/\s+/),
      textToHighlight: value
    });
  }, [contextValue, value]);
  return /* @__PURE__ */React.createElement(React.Fragment, null, results.length ? results.map(function (result, index) {
    var str = value.slice(result.start, result.end);
    return /* @__PURE__ */React.createElement("span", {
      key: index,
      "data-reach-combobox-option-text": "",
      "data-user-value": result.highlight ? true : void 0,
      "data-suggested-value": result.highlight ? void 0 : true
    }, str);
  }) : value);
}
ComboboxOptionText.displayName = "ComboboxOptionText";
var ComboboxButton = /*#__PURE__*/React.forwardRef(function (_ref10, forwardedRef) {
  var _ref10$as = _ref10.as,
    Comp = _ref10$as === void 0 ? "button" : _ref10$as,
    onClick = _ref10.onClick,
    onKeyDown = _ref10.onKeyDown,
    props = _objectWithoutProperties(_ref10, _excluded6);
  var _React$useContext7 = React.useContext(ComboboxContext),
    transition = _React$useContext7.transition,
    state = _React$useContext7.state,
    buttonRef = _React$useContext7.buttonRef,
    listboxId = _React$useContext7.listboxId,
    isExpanded = _React$useContext7.isExpanded;
  var ref = useComposedRefs(buttonRef, forwardedRef);
  var handleKeyDown = useKeyDown();
  var handleClick = function handleClick() {
    if (state === IDLE) {
      transition(OPEN_WITH_BUTTON);
    } else {
      transition(CLOSE_WITH_BUTTON);
    }
  };
  return /* @__PURE__ */React.createElement(Comp, _objectSpread(_objectSpread({
    "aria-controls": listboxId,
    "aria-haspopup": "listbox",
    "aria-expanded": isExpanded
  }, props), {}, {
    "data-reach-combobox-button": "",
    ref: ref,
    onClick: composeEventHandlers(onClick, handleClick),
    onKeyDown: composeEventHandlers(onKeyDown, handleKeyDown)
  }));
});
ComboboxButton.displayName = "ComboboxButton";
function useFocusManagement(lastEventType, inputRef) {
  useLayoutEffect(function () {
    if (lastEventType === NAVIGATE || lastEventType === ESCAPE || lastEventType === SELECT_WITH_CLICK || lastEventType === OPEN_WITH_BUTTON) {
      var _inputRef$current3;
      (_inputRef$current3 = inputRef.current) === null || _inputRef$current3 === void 0 ? void 0 : _inputRef$current3.focus();
    }
  }, [inputRef, lastEventType]);
}
function useKeyDown() {
  var _React$useContext8 = React.useContext(ComboboxContext),
    navigationValue = _React$useContext8.data.navigationValue,
    onSelect = _React$useContext8.onSelect,
    state = _React$useContext8.state,
    transition = _React$useContext8.transition,
    autocompletePropRef = _React$useContext8.autocompletePropRef,
    persistSelectionRef = _React$useContext8.persistSelectionRef,
    inputRef = _React$useContext8.inputRef,
    isControlledRef = _React$useContext8.isControlledRef;
  var options = useDescendants(ComboboxDescendantContext);
  return function handleKeyDown(event) {
    var _inputRef$current4;
    var index = options.findIndex(function (_ref11) {
      var value = _ref11.value;
      return value === navigationValue;
    });
    function getNextOption() {
      var atBottom = index === options.length - 1;
      if (atBottom) {
        if (autocompletePropRef.current) {
          return null;
        } else {
          return getFirstOption();
        }
      } else {
        return options[(index + 1) % options.length];
      }
    }
    function getPreviousOption() {
      var atTop = index === 0;
      if (atTop) {
        if (autocompletePropRef.current) {
          return null;
        } else {
          return getLastOption();
        }
      } else if (index === -1) {
        return getLastOption();
      } else {
        return options[(index - 1 + options.length) % options.length];
      }
    }
    function getFirstOption() {
      return options[0];
    }
    function getLastOption() {
      return options[options.length - 1];
    }
    var textareaHasMultilineValue = ((_inputRef$current4 = inputRef.current) === null || _inputRef$current4 === void 0 ? void 0 : _inputRef$current4.tagName.toUpperCase()) === "TEXTAREA" && inputRef.current.value.includes("\n");
    switch (event.key) {
      case "ArrowDown":
        if (!options || !options.length) {
          return;
        }
        if (state === IDLE) {
          if (textareaHasMultilineValue) {
            return;
          }
          event.preventDefault();
          transition(NAVIGATE, {
            persistSelection: persistSelectionRef.current
          });
        } else {
          event.preventDefault();
          var next = getNextOption();
          transition(NAVIGATE, {
            value: next ? next.value : null
          });
        }
        break;
      case "ArrowUp":
        if (!options || !options.length) {
          return;
        }
        if (state === IDLE) {
          if (textareaHasMultilineValue) {
            return;
          }
          event.preventDefault();
          transition(NAVIGATE, {
            persistSelection: persistSelectionRef.current
          });
        } else {
          event.preventDefault();
          var prev = getPreviousOption();
          transition(NAVIGATE, {
            value: prev ? prev.value : null
          });
        }
        break;
      case "Home":
      case "PageUp":
        event.preventDefault();
        if (!options || options.length === 0) {
          return;
        }
        if (state === IDLE) {
          transition(NAVIGATE);
        } else {
          transition(NAVIGATE, {
            value: getFirstOption().value
          });
        }
        break;
      case "End":
      case "PageDown":
        event.preventDefault();
        if (!options || options.length === 0) {
          return;
        }
        if (state === IDLE) {
          transition(NAVIGATE);
        } else {
          transition(NAVIGATE, {
            value: getLastOption().value
          });
        }
        break;
      case "Escape":
        if (state !== IDLE) {
          transition(ESCAPE);
        }
        break;
      case "Enter":
        if (state === NAVIGATING && navigationValue !== null) {
          event.preventDefault();
          onSelect && onSelect(navigationValue);
          transition(SELECT_WITH_KEYBOARD, {
            isControlled: isControlledRef.current
          });
        }
        break;
    }
  };
}
function useBlur() {
  var _React$useContext9 = React.useContext(ComboboxContext),
    state = _React$useContext9.state,
    transition = _React$useContext9.transition,
    popoverRef = _React$useContext9.popoverRef,
    inputRef = _React$useContext9.inputRef,
    buttonRef = _React$useContext9.buttonRef;
  return function handleBlur(event) {
    var popover = popoverRef.current;
    var input = inputRef.current;
    var button = buttonRef.current;
    var activeElement = event.relatedTarget;
    if (activeElement !== input && activeElement !== button && popover) {
      if (popover.contains(activeElement)) {
        if (state !== INTERACTING) {
          transition(INTERACT);
        }
      } else {
        transition(BLUR);
      }
    }
  };
}
function useReducerMachine(chart, reducer2, initialData) {
  var _React$useState = React.useState(chart.initial),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    state = _React$useState2[0],
    setState = _React$useState2[1];
  var _React$useReducer = React.useReducer(reducer2, initialData),
    _React$useReducer2 = _slicedToArray(_React$useReducer, 2),
    data = _React$useReducer2[0],
    dispatch = _React$useReducer2[1];
  var transition = function transition(event) {
    var payload = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    var currentState = chart.states[state];
    var nextState = currentState && currentState.on[event];
    if (nextState) {
      dispatch(_objectSpread({
        type: event,
        state: state,
        nextState: state
      }, payload));
      setState(nextState);
      return;
    }
  };
  return [state, data, transition];
}
function makeHash(str) {
  var hash = 0;
  if (str.length === 0) {
    return hash;
  }
  for (var i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash = hash & hash;
  }
  return hash;
}
function getDataState(state) {
  return state.toLowerCase();
}
function escapeRegexp(str) {
  return String(str).replace(/([.*+?=^!:${}()|[\]/\\])/g, "\\$1");
}
function useComboboxContext() {
  var _React$useContext10 = React.useContext(ComboboxContext),
    isExpanded = _React$useContext10.isExpanded,
    comboboxId = _React$useContext10.comboboxId,
    data = _React$useContext10.data,
    state = _React$useContext10.state;
  var navigationValue = data.navigationValue;
  return React.useMemo(function () {
    return {
      id: comboboxId,
      isExpanded: isExpanded,
      navigationValue: navigationValue !== null && navigationValue !== void 0 ? navigationValue : null,
      state: state
    };
  }, [comboboxId, isExpanded, navigationValue, state]);
}
function useComboboxOptionContext() {
  var _React$useContext11 = React.useContext(OptionContext),
    value = _React$useContext11.value,
    index = _React$useContext11.index;
  return React.useMemo(function () {
    return {
      value: value,
      index: index
    };
  }, [value, index]);
}
export { Combobox, ComboboxButton, ComboboxInput, ComboboxList, ComboboxOption, ComboboxOptionText, ComboboxPopover, escapeRegexp, useKeyDown as unstable_useKeyDown, useComboboxContext, useComboboxOptionContext };